import React from 'react';
import { Sparkles, BrainCircuit, Camera, Clapperboard, MapPin, ArrowRight, Users, Headphones, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

const teamMembers = [
  {
    name: "Apoorv Jha",
    role: "CTO",
    icon: BrainCircuit,
    image: "https://i.pinimg.com/564x/13/42/d4/1342d45e30d1d60937c15d4cff2e0115.jpg", 
    bio: "Building intelligent systems while staying competitive at the snooker table and TT court.",
    tagline: "Traveller by weekend • Biker at heart",
    color: "#0284c7",
    bg: "#e0f2fe",
    darkBg: "#082f49"
  },
  {
    name: "Aashish Patel",
    role: "Travel Consultant",
    icon: Camera,
    image: "https://www.worldauthors.org/wp-content/uploads/2023/08/Chacha-Chaudhary.jpg", 
    bio: "A travel enthusiast, photographer, tourist, and trekker. Bringing profound industry expertise to curate perfect journeys.",
    tagline: "15+ years in Travel & Hospitality",
    color: "#16a34a",
    bg: "#dcfce7",
    darkBg: "#064e3b"
  },
  {
    name: "Rakhi Soni",
    role: "Official Content Creator",
    icon: Clapperboard,
    image: "https://www.partysuppliesindia.com/cdn/shop/products/a1_40_5ccf2e61-57f8-45c9-b8d7-2743eb669b81.jpg?v=1735573930", 
    bio: "A vivid traveller with a relentless drive for original content ideas and storytelling.",
    tagline: "Dev by profession • Creator by passion • Traveller by obsession",
    color: "#be185d",
    bg: "#fce7f3",
    darkBg: "#500724"
  },
  {
    name: "Karan Kothari",
    role: "Official Content Creator",
    icon: Headphones,
    image: "https://cdn.dribbble.com/userupload/29089923/file/original-c038493c1f8d6c3e8c3fba8b31255100.png?resize=752x&vertical=center", 
    bio: "A branding professional with an exceptional eye for photography and a superb taste in music.",
    tagline: "Branding by day • Photographer by choice",
    color: "#9333ea",
    bg: "#f3e8ff",
    darkBg: "#3b0764"
  },
  {
    name: "Rishab Vaishya",
    role: "App Developer",
    icon: Smartphone,
    image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR5a2lt8-vU79h4X33n7fEhbSR1WomMkABA5l81Nu93Gw&s=10", 
    bio: "US-based coding expert who finds his true escape traversing the diverse and vibrant landscapes of India.",
    tagline: "Code from US • Heart in India",
    color: "#f59e0b",
    bg: "#fef3c7",
    darkBg: "#451a03"
  },
];

const MeetTheTeam = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme(); // 👈 Access Theme State

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', transition: 'background-color 0.3s' }}>
      
      {/* HERO SECTION */}
      <div style={{ position: 'relative', height: '40vh', minHeight: '350px', backgroundImage: 'url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.6), rgba(17, 24, 39, 0.9))' }} />
        
        <div style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', width: '100%', padding: '0 20px', boxSizing: 'border-box' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', padding: '8px 16px', borderRadius: '50px', color: 'white', fontSize: '14px', fontWeight: '600', marginBottom: '20px' }}>
            <Sparkles size={16} /> The Minds Behind Odessey
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: '900', color: 'white', marginBottom: '15px', letterSpacing: '-1px' }}>Meet Our Team</h1>
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.8)', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            We are a collective of developers, creators, and travel veterans united by one obsession: exploring the unseen.
          </p>
        </div>
      </div>

      {/* TEAM GRID */}
      <div style={{ maxWidth: '1200px', margin: '-40px auto 0', padding: '0 20px', position: 'relative', zIndex: 10, boxSizing: 'border-box' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px' }}>
          {teamMembers.map((member, idx) => (
            <div 
              key={idx} 
              style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', overflow: 'hidden', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s, border-color 0.3s', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = isDark ? '0 20px 40px rgba(0,0,0,0.7)' : '0 20px 40px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)'; }}
            >
              {/* Image Header */}
              <div style={{ height: '280px', position: 'relative', overflow: 'hidden' }}>
                <img src={member.image} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: '15px', right: '15px', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>
                  <member.icon size={22} color={member.color} />
                </div>
              </div>

              {/* Content Box */}
              <div style={{ padding: '25px', display: 'flex', flexDirection: 'column', flex: 1, boxSizing: 'border-box' }}>
                <div style={{ display: 'inline-block', backgroundColor: isDark ? member.darkBg : member.bg, color: member.color, padding: '5px 12px', borderRadius: '50px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', alignSelf: 'flex-start', marginBottom: '12px', letterSpacing: '0.5px' }}>
                  {member.role}
                </div>
                
                <h3 style={{ fontSize: '22px', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>
                  {member.name}
                </h3>
                
                <p style={{ fontSize: '14px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.6', marginBottom: '20px', flex: 1 }}>
                  {member.bio}
                </p>

                <div style={{ borderTop: isDark ? '1px solid #374151' : '1px solid #f3f4f6', paddingTop: '16px', marginTop: 'auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '13px', fontWeight: '600' }}>
                    <MapPin size={16} color={member.color} />
                    {member.tagline}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* BOTTOM CALL TO ACTION */}
        <div style={{ marginTop: '60px', backgroundColor: isDark ? '#030712' : '#111827', borderRadius: '24px', padding: '40px 20px', textAlign: 'center', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', border: isDark ? '1px solid #374151' : 'none', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 'bold', marginBottom: '12px' }}>Want to travel with us?</h2>
          <p style={{ color: '#9ca3af', fontSize: '15px', maxWidth: '500px', marginBottom: '25px', lineHeight: '1.5' }}>
            Our team has built the ultimate engine to help you discover and plan your next adventure across India.
          </p>
          <button 
            onClick={() => navigate('/discover')}
            style={{ padding: '14px 28px', borderRadius: '12px', backgroundColor: '#16a34a', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
            onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}
          >
            Discover Destinations <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeetTheTeam;